import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../../../api';
import { useMe } from '../../../hooks';
import { canEditResume } from '../../../shared/persona';
import {
  buildPrompt,
  fetchMe,
  issueGrant,
  saveProfile,
  uploadUrl,
  type ResumeAchievement,
  type ResumeConference,
  type ResumeEducation,
  type ResumeInternship,
  type ResumeMe,
  type ResumeProfile,
  type ResumeProject,
  type ResumeUploadRef,
  type TemplateInfo,
} from '../client';
import SearchSelect from '../components/SearchSelect';
import UploadField from '../components/UploadField';
import {
  DEGREE_OPTIONS,
  MAJOR_SUGGESTIONS,
  MONTH_OPTIONS,
  SCHOOL_SUGGESTIONS,
  YEAR_OPTIONS,
} from '../options';
import './console.css';

type EntryKind = 'project' | 'conference' | 'internship' | 'achievement';

type ProjectDraft = {
  name: string;
  role: string;
  start: string;
  end: string;
  ongoing: boolean;
  description: string;
  photo: ResumeUploadRef | null;
};

type ConferenceDraft = {
  name: string;
  topic: string;
  role: string;
  period: string;
  location: string;
  description: string;
  photo: ResumeUploadRef | null;
};

type InternshipDraft = {
  company: string;
  role: string;
  start: string;
  end: string;
  ongoing: boolean;
  description: string;
  photo: ResumeUploadRef | null;
};

type FormState = {
  displayName: string;
  grade: string;
  program: string;
  direction: string;
  summary: string;
  email: string;
  showEmail: boolean;
  portrait: ResumeUploadRef | null;
  education: ResumeEducation[];
  skills: string[];
  projects: ResumeProject[];
  conferences: ResumeConference[];
  internships: ResumeInternship[];
  achievements: ResumeAchievement[];
};

type EditorSection =
  | 'basics'
  | 'education'
  | 'skills'
  | 'projects'
  | 'conferences'
  | 'internships'
  | 'achievements';

type DraftErrors = Partial<Record<'education' | 'project' | 'conference' | 'internship', string>>;

type EntryFormKind = keyof DraftErrors;

type GuideItem = {
  id: string;
  section: EditorSection;
  label: string;
  hint: string;
  done: boolean;
  optional?: boolean;
  parent?: string;
};

const DRAFT_STORAGE_PREFIX = 'ccs-resume-entry-drafts:';

const EMPTY_EDUCATION: ResumeEducation = {
  school: '',
  degree: '',
  major: '',
  start: '',
  end: '',
};

const EMPTY_PROJECT_DRAFT: ProjectDraft = {
  name: '',
  role: '',
  start: '',
  end: '',
  ongoing: false,
  description: '',
  photo: null,
};

const EMPTY_CONFERENCE_DRAFT: ConferenceDraft = {
  name: '',
  topic: '',
  role: '',
  period: '',
  location: '',
  description: '',
  photo: null,
};

const EMPTY_INTERNSHIP_DRAFT: InternshipDraft = {
  company: '',
  role: '',
  start: '',
  end: '',
  ongoing: false,
  description: '',
  photo: null,
};

const EMPTY_ACHIEVEMENT: ResumeAchievement = {
  title: '',
  date: '',
  description: '',
  material: null,
};

const CONFERENCE_ROLE_OPTIONS = ['口头报告', '海报展示', '特邀报告', '分会主持', '仅参会', '其他'];

function parsePeriod(period: string): Pick<ProjectDraft, 'start' | 'end' | 'ongoing'> {
  const text = period.trim();
  if (!text) return { start: '', end: '', ongoing: false };
  const ongoing = /至今|现在|目前|present|current/i.test(text);
  const cleaned = text
    .replace(/至今|现在|目前|present|current/gi, '')
    .replace(/\s*(?:-|–|—|~|～|至|到)\s*$/u, '')
    .trim();
  const pieces = cleaned.split(/\s*(?:-|–|—|~|～|至|到)\s*/u).filter(Boolean);
  return { start: pieces[0] ?? '', end: ongoing ? '' : pieces[1] ?? '', ongoing };
}

function formatPeriod(parts: { start: string; end: string; ongoing: boolean }): string {
  const start = parts.start.trim();
  const end = parts.end.trim();
  if (parts.ongoing && start) return `${start} - 至今`;
  if (start && end) return `${start} - ${end}`;
  return start || end;
}

function projectToDraft(project: ResumeProject): ProjectDraft {
  return {
    name: project.name,
    role: project.role,
    ...parsePeriod(project.period),
    description: project.description,
    photo: project.photo,
  };
}

function projectFromDraft(draft: ProjectDraft): ResumeProject {
  return {
    name: draft.name.trim(),
    role: draft.role.trim(),
    period: formatPeriod(draft),
    description: draft.description.trim(),
    photo: draft.photo,
  };
}

function hasProjectContent(project: ResumeProject): boolean {
  return Boolean(project.name || project.role || project.period || project.description || project.photo);
}

function conferenceToDraft(item: ResumeConference): ConferenceDraft {
  return {
    name: item.name,
    topic: item.topic,
    role: item.role,
    period: item.period,
    location: item.location,
    description: item.description,
    photo: item.photo,
  };
}

function conferenceFromDraft(draft: ConferenceDraft): ResumeConference {
  return {
    name: draft.name.trim(),
    topic: draft.topic.trim(),
    role: draft.role.trim(),
    period: draft.period.trim(),
    location: draft.location.trim(),
    description: draft.description.trim(),
    photo: draft.photo,
  };
}

function hasConferenceContent(item: ResumeConference): boolean {
  return Boolean(
    item.name || item.topic || item.role || item.period || item.location || item.description || item.photo,
  );
}

function internshipToDraft(item: ResumeInternship): InternshipDraft {
  return {
    company: item.company,
    role: item.role,
    ...parsePeriod(item.period),
    description: item.description,
    photo: item.photo,
  };
}

function internshipFromDraft(draft: InternshipDraft): ResumeInternship {
  return {
    company: draft.company.trim(),
    role: draft.role.trim(),
    period: formatPeriod(draft),
    description: draft.description.trim(),
    photo: draft.photo,
  };
}

function hasInternshipContent(item: ResumeInternship): boolean {
  return Boolean(item.company || item.role || item.period || item.description || item.photo);
}

function upsert<T>(items: T[], entry: T, index: number | null): T[] {
  return index === null
    ? [...items, entry]
    : items.map((item, position) => (position === index ? entry : item));
}

function hasEducationContent(entry: ResumeEducation): boolean {
  return Boolean(entry.school || entry.degree || entry.major || entry.start || entry.end);
}

function draftPeriodError(start: string, end: string): string {
  if (!start || !end) return '';
  return end < start ? '结束时间不能早于开始时间。' : '';
}

function isEducationDraftEmpty(value: ResumeEducation): boolean {
  return !hasEducationContent(value);
}

function isProjectDraftEmpty(value: ProjectDraft): boolean {
  return !hasProjectContent(projectFromDraft(value));
}

function isConferenceDraftEmpty(value: ConferenceDraft): boolean {
  return !hasConferenceContent(conferenceFromDraft(value));
}

function isInternshipDraftEmpty(value: InternshipDraft): boolean {
  return !hasInternshipContent(internshipFromDraft(value));
}

function isSkillDraftEmpty(value: string): boolean {
  return !value.trim();
}

function mergeDraftObject<T extends object>(emptyValue: T, saved: unknown): T {
  return saved && typeof saved === 'object' ? { ...emptyValue, ...(saved as Partial<T>) } : emptyValue;
}

function mergeDraftString(emptyValue: string, saved: unknown): string {
  return typeof saved === 'string' ? saved : emptyValue;
}

function useStoredDraft<T>(
  storageKey: string,
  emptyValue: T,
  isEmpty: (value: T) => boolean,
  merge: (emptyValue: T, saved: unknown) => T,
) {
  const [value, setValue] = useState<T>(emptyValue);
  const loadedKeyRef = useRef('');
  useEffect(() => {
    if (!storageKey || loadedKeyRef.current === storageKey) return;
    loadedKeyRef.current = storageKey;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw) {
        setValue(merge(emptyValue, JSON.parse(raw) as unknown));
        return;
      }
    } catch {
      // 忽略损坏或不可用的本地存储
    }
    setValue(emptyValue);
  }, [storageKey, emptyValue, merge]);
  useEffect(() => {
    if (!storageKey || loadedKeyRef.current !== storageKey) return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(value));
    } catch {
      // 隐私模式下存储可能不可用，草稿只保留在内存中
    }
  }, [storageKey, value]);
  return [value, setValue, !isEmpty(value)] as const;
}

async function writeClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // 回退到 execCommand
  }
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const copied = document.execCommand('copy');
    area.remove();
    return copied;
  } catch {
    return false;
  }
}

function focusField(id: string): void {
  window.requestAnimationFrame(() => document.getElementById(id)?.focus());
}

const EMPTY_FORM: FormState = {
  displayName: '',
  grade: '',
  program: '',
  direction: '',
  summary: '',
  email: '',
  showEmail: false,
  portrait: null,
  education: [],
  skills: [],
  projects: [],
  conferences: [],
  internships: [],
  achievements: [],
};

type SavePayload = Parameters<typeof saveProfile>[0];

type SaveVariables = {
  payload: SavePayload;
  snapshot: string;
  manual: boolean;
};

function toPayload(state: FormState): SavePayload {
  return {
    displayName: state.displayName,
    grade: state.grade,
    program: state.program,
    direction: state.direction,
    summary: state.summary,
    portrait: state.portrait,
    education: state.education,
    skills: state.skills,
    projects: state.projects.filter(hasProjectContent),
    conferences: state.conferences.filter(hasConferenceContent),
    internships: state.internships.filter(hasInternshipContent),
    achievements: state.achievements.filter(
      (item) => item.title || item.date || item.description || item.material,
    ),
    email: state.email,
    showEmail: state.showEmail,
  };
}

function toPromptProfile(profile: ResumeProfile, state: FormState): ResumeProfile {
  const payload = toPayload(state);
  return {
    ...profile,
    display_name: payload.displayName ?? '',
    grade: payload.grade ?? '',
    program: payload.program ?? '',
    direction: payload.direction ?? '',
    summary: payload.summary ?? '',
    email: payload.email || null,
    showEmail: payload.showEmail ?? false,
    portrait: payload.portrait ?? null,
    education: payload.education ?? [],
    skills: payload.skills ?? [],
    projects: payload.projects ?? [],
    conferences: payload.conferences ?? [],
    internships: payload.internships ?? [],
    achievements: payload.achievements ?? [],
  };
}

const STATUS_LABEL: Record<string, string> = {
  draft: '草稿',
  published: '已上线',
  hidden: '已隐藏',
  pending: '待审核',
  rejected: '已驳回',
  superseded: '已被新版本替代',
};

const PUBLISH_LABEL: Record<string, string> = {
  pending: '待审核',
  published: '已上线',
  rejected: '已驳回',
  superseded: '已被替代',
  hidden: '已隐藏',
};

function YearMonthSelect({
  id,
  value,
  onChange,
  disabled = false,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const separator = value.indexOf('.');
  const year = separator === -1 ? value : value.slice(0, separator);
  const month = separator === -1 ? '' : value.slice(separator + 1);
  const years = year && !YEAR_OPTIONS.includes(year) ? [year, ...YEAR_OPTIONS] : YEAR_OPTIONS;
  const months =
    month && !MONTH_OPTIONS.some((option) => option.value === month)
      ? [{ value: month, label: month }, ...MONTH_OPTIONS]
      : MONTH_OPTIONS;

  const updateYear = (nextYear: string) => {
    if (!nextYear) {
      onChange('');
      return;
    }
    onChange(month ? `${nextYear}.${month}` : nextYear);
  };

  return (
    <div className="resume-date-row">
      <select
        id={`${id}-year`}
        value={year}
        disabled={disabled}
        onChange={(event) => updateYear(event.target.value)}
      >
        <option value="">年份</option>
        {years.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <select
        id={`${id}-month`}
        aria-label="月份"
        value={month}
        disabled={disabled || !year}
        onChange={(event) => onChange(event.target.value ? `${year}.${event.target.value}` : year)}
      >
        <option value="">月份</option>
        {months.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function EntryActions({
  confirming,
  onEdit,
  onDelete,
  onConfirm,
  onCancel,
}: {
  confirming: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!confirming) {
    return (
      <div className="resume-entry-actions">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onEdit}>
          编辑
        </button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onDelete}>
          删除
        </button>
      </div>
    );
  }
  return (
    <div className="resume-entry-actions resume-entry-actions-confirm">
      <span className="resume-delete-hint">删除后不可恢复</span>
      <button type="button" className="btn btn-danger btn-sm" onClick={onConfirm}>
        确认删除
      </button>
      <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>
        取消
      </button>
    </div>
  );
}

const ERROR_LABELS: Record<string, string> = {
  invalid_request: '填写内容不符合要求，请检查格式。',
  forbidden: '当前角色没有这个操作权限。',
  not_eligible: '当前账号还没有发布权限。',
  grant_invalid: '上传权限已失效，请重新复制提示词。',
  grant_scope_mismatch: '上传权限与当前简历不匹配。',
  profile_not_found: '还没有为你建立简历资料。',
  asset_too_large: '文件超过大小或尺寸限制。',
  material_too_large: '材料超过 4 MB。',
  pdf_not_allowed: '只有成果材料可以上传 PDF。',
  expected_file: '没有收到有效的文件。',
};

function errorText(error: unknown): string {
  if (error instanceof ApiError) return ERROR_LABELS[error.code] ?? error.code;
  if (error instanceof Error) return error.message;
  return '操作失败';
}

function formatTime(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('zh-CN', { hour12: false });
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatCountdown(seconds: number): string {
  const safe = Math.max(0, seconds);
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, '0')}`;
}

export default function ResumeWorkbench() {
  const queryClient = useQueryClient();
  const auth = useMe();
  const [token, setToken] = useState('');
  const tokenRef = useRef('');
  const tokenExpiresRef = useRef(0);
  const [grantLeft, setGrantLeft] = useState(0);
  const [copyingId, setCopyingId] = useState('');
  const [copiedId, setCopiedId] = useState('');
  const me = useQuery<ResumeMe>({
    queryKey: ['resume-me'],
    queryFn: fetchMe,
    refetchInterval: token ? 10_000 : false,
  });
  const profile = me.data?.profile ?? null;
  const storageSlug = me.data?.profile?.slug ?? '';
  const draftStorageKey = (part: string) =>
    storageSlug ? `${DRAFT_STORAGE_PREFIX}${storageSlug}:${part}` : '';
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [educationDraft, setEducationDraft, educationDraftHasContent] = useStoredDraft(
    draftStorageKey('education'),
    EMPTY_EDUCATION,
    isEducationDraftEmpty,
    mergeDraftObject,
  );
  const [editingEducation, setEditingEducation] = useState<number | null>(null);
  const [skillDraft, setSkillDraft, skillDraftHasContent] = useStoredDraft(
    draftStorageKey('skill'),
    '',
    isSkillDraftEmpty,
    mergeDraftString,
  );
  const [projectDraft, setProjectDraft, projectDraftHasContent] = useStoredDraft(
    draftStorageKey('project'),
    EMPTY_PROJECT_DRAFT,
    isProjectDraftEmpty,
    mergeDraftObject,
  );
  const [editingProject, setEditingProject] = useState<number | null>(null);
  const [conferenceDraft, setConferenceDraft, conferenceDraftHasContent] = useStoredDraft(
    draftStorageKey('conference'),
    EMPTY_CONFERENCE_DRAFT,
    isConferenceDraftEmpty,
    mergeDraftObject,
  );
  const [editingConference, setEditingConference] = useState<number | null>(null);
  const [internshipDraft, setInternshipDraft, internshipDraftHasContent] = useStoredDraft(
    draftStorageKey('internship'),
    EMPTY_INTERNSHIP_DRAFT,
    isInternshipDraftEmpty,
    mergeDraftObject,
  );
  const [editingInternship, setEditingInternship] = useState<number | null>(null);
  const [entryForm, setEntryForm] = useState<EntryFormKind | null>(null);
  const pendingFocusRef = useRef('');
  const restoredDraftFormRef = useRef(false);
  const [pendingDelete, setPendingDelete] = useState<{ kind: EntryKind; index: number } | null>(null);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [errors, setErrors] = useState<DraftErrors>({});
  const [saveState, setSaveState] = useState<
    'idle' | 'pending' | 'saving' | 'saved' | 'error' | 'blocked'
  >('idle');
  const [saveError, setSaveError] = useState('');
  const hydratedRef = useRef(false);
  const lastSavedRef = useRef('');
  const failedSnapshotRef = useRef('');
  const sectionRefs = useRef<Partial<Record<EditorSection, HTMLElement | null>>>({});

  useEffect(() => {
    if (!profile || hydratedRef.current) return;
    hydratedRef.current = true;
    const next: FormState = {
      displayName: profile.display_name,
      grade: profile.grade,
      program: profile.program,
      direction: profile.direction,
      summary: profile.summary,
      email: profile.email ?? '',
      showEmail: profile.showEmail,
      portrait: profile.portrait,
      education: profile.education.map((item) => ({ ...item })),
      skills: [...profile.skills],
      projects: profile.projects.map((project) => ({ ...project })),
      conferences: profile.conferences.map((item) => ({ ...item })),
      internships: profile.internships.map((item) => ({ ...item })),
      achievements: profile.achievements.map((item) => ({ ...item })),
    };
    setForm(next);
    lastSavedRef.current = JSON.stringify(toPayload(next));
  }, [profile]);

  useEffect(() => {
    if (restoredDraftFormRef.current || entryForm) return;
    const kind: EntryFormKind | null = educationDraftHasContent
      ? 'education'
      : projectDraftHasContent
        ? 'project'
        : conferenceDraftHasContent
          ? 'conference'
          : internshipDraftHasContent
            ? 'internship'
            : null;
    if (!kind) return;
    restoredDraftFormRef.current = true;
    setEntryForm(kind);
  }, [
    conferenceDraftHasContent,
    educationDraftHasContent,
    entryForm,
    internshipDraftHasContent,
    projectDraftHasContent,
  ]);

  useEffect(() => {
    const release = () => {
      if (!tokenRef.current) return;
      tokenRef.current = '';
      tokenExpiresRef.current = 0;
      void fetch('/api/resume/grant', {
        method: 'DELETE',
        credentials: 'same-origin',
        keepalive: true,
      }).catch(() => undefined);
    };
    const restore = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      tokenRef.current = '';
      tokenExpiresRef.current = 0;
      setToken('');
    };
    window.addEventListener('pagehide', release);
    window.addEventListener('pageshow', restore);
    return () => {
      window.removeEventListener('pagehide', release);
      window.removeEventListener('pageshow', restore);
    };
  }, []);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['resume-me'] });

  const save = useMutation({
    mutationFn: (variables: SaveVariables) => saveProfile(variables.payload),
    onSuccess: (_data, variables) => {
      lastSavedRef.current = variables.snapshot;
      failedSnapshotRef.current = '';
      setSaveState('saved');
      if (variables.manual) {
        setNotice({ kind: 'ok', text: '资料已保存。草稿不会公网可见，发布后才会更新列表。' });
      }
      refresh();
    },
    onError: (error, variables) => {
      failedSnapshotRef.current = variables.snapshot;
      setSaveState('error');
      setSaveError(errorText(error));
      if (variables.manual) {
        setNotice({ kind: 'err', text: `保存失败：${errorText(error)}` });
      }
    },
  });

  const autoPayload = useMemo(() => toPayload(form), [form]);
  const autoSnapshot = useMemo(() => JSON.stringify(autoPayload), [autoPayload]);

  const missingName = !form.displayName.trim();

  useEffect(() => {
    if (!hydratedRef.current) return;
    if (missingName) {
      setSaveState('blocked');
      return;
    }
    if (autoSnapshot === lastSavedRef.current) {
      setSaveState((current) => (current === 'blocked' || current === 'error' ? 'saved' : current));
      return;
    }
    if (autoSnapshot === failedSnapshotRef.current) return;
    if (save.isPending) return;
    setSaveState('pending');
    const timer = window.setTimeout(() => {
      setSaveState('saving');
      save.mutate({ payload: autoPayload, snapshot: autoSnapshot, manual: false });
    }, 900);
    return () => window.clearTimeout(timer);
  }, [autoPayload, autoSnapshot, missingName, save.isPending, save.mutate]);

  useEffect(() => {
    if (!token) {
      setGrantLeft(0);
      return;
    }
    const tick = () =>
      setGrantLeft(Math.max(0, Math.floor((tokenExpiresRef.current - Date.now()) / 1000)));
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [token]);

  useEffect(() => {
    if (!copiedId) return;
    const timer = window.setTimeout(() => setCopiedId(''), 2500);
    return () => window.clearTimeout(timer);
  }, [copiedId]);

  const pendingId = me.data?.pending?.id ?? null;
  useEffect(() => {
    if (!pendingId) return;
    const timer = window.setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: ['resume-me'] });
    }, 10_000);
    return () => window.clearInterval(timer);
  }, [pendingId, queryClient]);

  const updateEducationDraft = (key: keyof ResumeEducation, value: string) => {
    setEducationDraft((current) => ({ ...current, [key]: value }));
    if (errors.education) setErrors((current) => ({ ...current, education: '' }));
  };

  const normalizeEducation = (entry: ResumeEducation): ResumeEducation => ({
    school: entry.school.trim(),
    degree: entry.degree.trim(),
    major: entry.major.trim(),
    start: entry.start.trim(),
    end: entry.end.trim(),
  });

  const educationError = (entry: ResumeEducation): string => {
    if (!entry.school) return '请先填写学校名称。';
    return draftPeriodError(entry.start, entry.end);
  };

  const openEntryForm = (kind: EntryFormKind) => {
    const firstField: Record<EntryFormKind, string> = {
      education: 'resume-school',
      project: 'resume-project-name',
      conference: 'resume-conference-name',
      internship: 'resume-internship-company',
    };
    pendingFocusRef.current = firstField[kind];
    setEntryForm(kind);
    setErrors((current) => ({ ...current, [kind]: '' }));
  };

  useEffect(() => {
    const id = pendingFocusRef.current;
    if (!entryForm || !id) return;
    pendingFocusRef.current = '';
    document.getElementById(id)?.focus();
  }, [entryForm]);

  const commitEducation = () => {
    const entry = normalizeEducation(educationDraft);
    const error = educationError(entry);
    if (error) {
      setErrors((current) => ({ ...current, education: error }));
      focusField(!entry.school ? 'resume-school' : 'resume-edu-start-year');
      return;
    }
    setForm((current) => ({
      ...current,
      education:
        editingEducation === null
          ? [...current.education, entry]
          : current.education.map((item, index) => (index === editingEducation ? entry : item)),
    }));
    setEducationDraft({ ...EMPTY_EDUCATION });
    setEditingEducation(null);
    setEntryForm(null);
    setErrors((current) => ({ ...current, education: '' }));
  };

  const editEducation = (index: number) => {
    setEducationDraft({ ...form.education[index] });
    setEditingEducation(index);
    openEntryForm('education');
  };

  const cancelEducationEdit = () => {
    setEducationDraft({ ...EMPTY_EDUCATION });
    setEditingEducation(null);
    setEntryForm(null);
  };

  const removeEducation = (index: number) => {
    setForm((current) => ({
      ...current,
      education: current.education.filter((_, position) => position !== index),
    }));
    if (editingEducation === index) {
      setEducationDraft({ ...EMPTY_EDUCATION });
      setEditingEducation(null);
      setEntryForm(null);
    } else if (editingEducation !== null && editingEducation > index) {
      setEditingEducation(editingEducation - 1);
    }
  };

  const addSkill = () => {
    const value = skillDraft.trim();
    if (!value) return;
    setForm((current) =>
      current.skills.includes(value) ? current : { ...current, skills: [...current.skills, value] },
    );
    setSkillDraft('');
  };

  const removeSkill = (index: number) => {
    setForm((current) => ({
      ...current,
      skills: current.skills.filter((_, position) => position !== index),
    }));
  };

  const submitDraft = () => {
    if (!form.displayName.trim()) {
      setNotice({ kind: 'err', text: '请先填写姓名，再保存。' });
      focusField('resume-name');
      return;
    }

    const nextErrors: DraftErrors = {};
    const educationEntry = normalizeEducation(educationDraft);
    if (hasEducationContent(educationEntry)) {
      const error = educationError(educationEntry);
      if (error) nextErrors.education = error;
    }
    const projectEntry = projectFromDraft(projectDraft);
    if (hasProjectContent(projectEntry)) {
      if (!projectEntry.name) nextErrors.project = '请先填写项目名称。';
      else nextErrors.project = draftPeriodError(projectDraft.start, projectDraft.end);
    }
    const conferenceEntry = conferenceFromDraft(conferenceDraft);
    if (hasConferenceContent(conferenceEntry) && !conferenceEntry.name) {
      nextErrors.conference = '请先填写会议名称。';
    }
    const internshipEntry = internshipFromDraft(internshipDraft);
    if (hasInternshipContent(internshipEntry)) {
      if (!internshipEntry.company) nextErrors.internship = '请先填写单位名称。';
      else nextErrors.internship = draftPeriodError(internshipDraft.start, internshipDraft.end);
    }

    if (nextErrors.education) {
      setErrors(nextErrors);
      setNotice({ kind: 'err', text: '还有内容没有填写完整，请检查下方标记。' });
      focusField(nextErrors.education === '请先填写学校名称。' ? 'resume-school' : 'resume-edu-start-year');
      return;
    }
    if (nextErrors.project) {
      setErrors(nextErrors);
      setNotice({ kind: 'err', text: '还有内容没有填写完整，请检查下方标记。' });
      focusField(nextErrors.project === '请先填写项目名称。' ? 'resume-project-name' : 'resume-project-start-year');
      return;
    }
    if (nextErrors.conference) {
      setErrors(nextErrors);
      setNotice({ kind: 'err', text: '还有内容没有填写完整，请检查下方标记。' });
      focusField('resume-conference-name');
      return;
    }
    if (nextErrors.internship) {
      setErrors(nextErrors);
      setNotice({ kind: 'err', text: '还有内容没有填写完整，请检查下方标记。' });
      focusField(nextErrors.internship === '请先填写单位名称。' ? 'resume-internship-company' : 'resume-internship-start-year');
      return;
    }

    const education = hasEducationContent(educationEntry)
      ? editingEducation === null
        ? [...form.education, educationEntry]
        : form.education.map((item, index) => (index === editingEducation ? educationEntry : item))
      : form.education;
    const skill = skillDraft.trim();
    const skills = skill && !form.skills.includes(skill) ? [...form.skills, skill] : form.skills;
    const projects = hasProjectContent(projectEntry)
      ? upsert(form.projects, projectEntry, editingProject)
      : form.projects;
    const conferences = hasConferenceContent(conferenceEntry)
      ? upsert(form.conferences, conferenceEntry, editingConference)
      : form.conferences;
    const internships = hasInternshipContent(internshipEntry)
      ? upsert(form.internships, internshipEntry, editingInternship)
      : form.internships;

    const nextForm: FormState = {
      ...form,
      education,
      skills,
      projects,
      conferences,
      internships,
    };
    setForm(nextForm);
    setErrors({});
    setEducationDraft({ ...EMPTY_EDUCATION });
    setEditingEducation(null);
    setSkillDraft('');
    setProjectDraft({ ...EMPTY_PROJECT_DRAFT });
    setEditingProject(null);
    setConferenceDraft({ ...EMPTY_CONFERENCE_DRAFT });
    setEditingConference(null);
    setInternshipDraft({ ...EMPTY_INTERNSHIP_DRAFT });
    setEditingInternship(null);
    setEntryForm(null);
    const payload = toPayload(nextForm);
    save.mutate({ payload, snapshot: JSON.stringify(payload), manual: true });
  };

  const updateProjectDraft = (patch: Partial<ProjectDraft>) => {
    setProjectDraft((current) => ({ ...current, ...patch }));
    if (errors.project) setErrors((current) => ({ ...current, project: '' }));
  };

  const commitProject = () => {
    const entry = projectFromDraft(projectDraft);
    if (!entry.name) {
      setErrors((current) => ({ ...current, project: '请先填写项目名称。' }));
      focusField('resume-project-name');
      return;
    }
    const error = draftPeriodError(projectDraft.start, projectDraft.end);
    if (error) {
      setErrors((current) => ({ ...current, project: error }));
      focusField('resume-project-start-year');
      return;
    }
    setForm((current) => ({ ...current, projects: upsert(current.projects, entry, editingProject) }));
    setProjectDraft({ ...EMPTY_PROJECT_DRAFT });
    setEditingProject(null);
    setEntryForm(null);
    setErrors((current) => ({ ...current, project: '' }));
  };

  const editProject = (index: number) => {
    setPendingDelete(null);
    setProjectDraft(projectToDraft(form.projects[index]));
    setEditingProject(index);
    openEntryForm('project');
  };

  const cancelProjectEdit = () => {
    setProjectDraft({ ...EMPTY_PROJECT_DRAFT });
    setEditingProject(null);
    setEntryForm(null);
  };

  const updateConferenceDraft = (patch: Partial<ConferenceDraft>) => {
    setConferenceDraft((current) => ({ ...current, ...patch }));
    if (errors.conference) setErrors((current) => ({ ...current, conference: '' }));
  };

  const commitConference = () => {
    const entry = conferenceFromDraft(conferenceDraft);
    if (!entry.name) {
      setErrors((current) => ({ ...current, conference: '请先填写会议名称。' }));
      focusField('resume-conference-name');
      return;
    }
    setForm((current) => ({
      ...current,
      conferences: upsert(current.conferences, entry, editingConference),
    }));
    setConferenceDraft({ ...EMPTY_CONFERENCE_DRAFT });
    setEditingConference(null);
    setEntryForm(null);
    setErrors((current) => ({ ...current, conference: '' }));
  };

  const editConference = (index: number) => {
    setPendingDelete(null);
    setConferenceDraft(conferenceToDraft(form.conferences[index]));
    setEditingConference(index);
    openEntryForm('conference');
  };

  const cancelConferenceEdit = () => {
    setConferenceDraft({ ...EMPTY_CONFERENCE_DRAFT });
    setEditingConference(null);
    setEntryForm(null);
  };

  const updateInternshipDraft = (patch: Partial<InternshipDraft>) => {
    setInternshipDraft((current) => ({ ...current, ...patch }));
    if (errors.internship) setErrors((current) => ({ ...current, internship: '' }));
  };

  const commitInternship = () => {
    const entry = internshipFromDraft(internshipDraft);
    if (!entry.company) {
      setErrors((current) => ({ ...current, internship: '请先填写单位名称。' }));
      focusField('resume-internship-company');
      return;
    }
    const error = draftPeriodError(internshipDraft.start, internshipDraft.end);
    if (error) {
      setErrors((current) => ({ ...current, internship: error }));
      focusField('resume-internship-start-year');
      return;
    }
    setForm((current) => ({
      ...current,
      internships: upsert(current.internships, entry, editingInternship),
    }));
    setInternshipDraft({ ...EMPTY_INTERNSHIP_DRAFT });
    setEditingInternship(null);
    setEntryForm(null);
    setErrors((current) => ({ ...current, internship: '' }));
  };

  const editInternship = (index: number) => {
    setPendingDelete(null);
    setInternshipDraft(internshipToDraft(form.internships[index]));
    setEditingInternship(index);
    openEntryForm('internship');
  };

  const cancelInternshipEdit = () => {
    setInternshipDraft({ ...EMPTY_INTERNSHIP_DRAFT });
    setEditingInternship(null);
    setEntryForm(null);
  };

  const requestDelete = (kind: EntryKind, index: number) => {
    setPendingDelete({ kind, index });
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;
    const { kind, index } = pendingDelete;
    if (kind === 'project') {
      setForm((current) => ({
        ...current,
        projects: current.projects.filter((_, position) => position !== index),
      }));
      if (editingProject === index) {
        setProjectDraft({ ...EMPTY_PROJECT_DRAFT });
        setEditingProject(null);
        setEntryForm(null);
      } else if (editingProject !== null && editingProject > index) {
        setEditingProject(editingProject - 1);
      }
    } else if (kind === 'conference') {
      setForm((current) => ({
        ...current,
        conferences: current.conferences.filter((_, position) => position !== index),
      }));
      if (editingConference === index) {
        setConferenceDraft({ ...EMPTY_CONFERENCE_DRAFT });
        setEditingConference(null);
        setEntryForm(null);
      } else if (editingConference !== null && editingConference > index) {
        setEditingConference(editingConference - 1);
      }
    } else if (kind === 'internship') {
      setForm((current) => ({
        ...current,
        internships: current.internships.filter((_, position) => position !== index),
      }));
      if (editingInternship === index) {
        setInternshipDraft({ ...EMPTY_INTERNSHIP_DRAFT });
        setEditingInternship(null);
        setEntryForm(null);
      } else if (editingInternship !== null && editingInternship > index) {
        setEditingInternship(editingInternship - 1);
      }
    } else {
      setForm((current) => ({
        ...current,
        achievements: current.achievements.filter((_, position) => position !== index),
      }));
    }
    setPendingDelete(null);
  };

  const addAchievement = () => {
    setForm((current) => ({ ...current, achievements: [...current.achievements, { ...EMPTY_ACHIEVEMENT }] }));
  };

  const updateAchievement = (index: number, patch: Partial<ResumeAchievement>) => {
    setForm((current) => ({
      ...current,
      achievements: current.achievements.map((item, position) =>
        position === index ? { ...item, ...patch } : item,
      ),
    }));
  };

  const ensureToken = async (): Promise<string> => {
    if (tokenRef.current && tokenExpiresRef.current > Date.now() + 5_000) {
      return tokenRef.current;
    }
    const grant = await issueGrant();
    tokenRef.current = grant.token;
    tokenExpiresRef.current = Date.parse(grant.expiresAt);
    setToken(grant.token);
    return grant.token;
  };

  const copyPrompt = async (template: TemplateInfo) => {
    if (!profile) return;
    if (!form.displayName.trim()) {
      setNotice({ kind: 'err', text: '请先填写姓名，再复制提示词。' });
      focusField('resume-name');
      return;
    }
    if (!profile.canPublish) {
      setNotice({ kind: 'err', text: '管理员还没有给你发布权限。' });
      return;
    }
    setCopyingId(template.id);
    setNotice(null);
    try {
      const activeToken = await ensureToken();
      const text = buildPrompt({
        profile: toPromptProfile(profile, form),
        template,
        slug: profile.slug,
        token: activeToken,
        origin: window.location.origin,
      });
      const copied = await writeClipboard(text);
      if (!copied) {
        setNotice({ kind: 'err', text: '浏览器不允许自动复制，请再点一次或手动复制提示词。' });
        return;
      }
      setCopiedId(template.id);
      setNotice({
        kind: 'ok',
        text: `已复制「${template.name}」提示词，上传权限已获取。下一步：粘贴给 AI 并保持本页打开，AI 上传后右上角会出现预览入口。`,
      });
      refresh();
    } catch (error) {
      setNotice({ kind: 'err', text: `复制失败：${errorText(error)}` });
    } finally {
      setCopyingId('');
    }
  };

  const templates = useMemo(() => me.data?.templates ?? [], [me.data?.templates]);

  const schoolOptions = useMemo(() => {
    const used = form.education.map((item) => item.school).filter(Boolean);
    return Array.from(new Set([...used, ...SCHOOL_SUGGESTIONS]));
  }, [form.education]);

  const majorOptions = useMemo(() => {
    const used = form.education.map((item) => item.major).filter(Boolean);
    return Array.from(new Set([...used, ...MAJOR_SUGGESTIONS]));
  }, [form.education]);

  const degreeOptions = useMemo(() => {
    const options: string[] = [...DEGREE_OPTIONS];
    if (educationDraft.degree && !options.includes(educationDraft.degree)) {
      options.unshift(educationDraft.degree);
    }
    return options;
  }, [educationDraft.degree]);

  const conferenceRoleOptions = useMemo(() => {
    const options = [...CONFERENCE_ROLE_OPTIONS];
    if (conferenceDraft.role && !options.includes(conferenceDraft.role)) {
      options.unshift(conferenceDraft.role);
    }
    return options;
  }, [conferenceDraft.role]);

  const guide = useMemo<GuideItem[]>(() => {
    const basicsDone = Boolean(
      form.displayName.trim() && form.grade.trim() && form.program.trim() && form.direction.trim(),
    );
    return [
      {
        id: 'basics',
        section: 'basics',
        label: '基本资料',
        hint: '姓名、年级、专业和研究方向',
        done: basicsDone,
      },
      {
        id: 'summary',
        section: 'basics',
        label: '个人简介',
        hint: '一两句话概括你的研究',
        done: form.summary.trim().length >= 10,
        optional: true,
        parent: 'basics',
      },
      {
        id: 'portrait',
        section: 'basics',
        label: '个人照片',
        hint: '上传清晰头像，简历更完整',
        done: Boolean(form.portrait),
        optional: true,
        parent: 'basics',
      },
      {
        id: 'education',
        section: 'education',
        label: '教育背景',
        hint: '至少一条教育经历',
        done: form.education.length > 0,
      },
      {
        id: 'skills',
        section: 'skills',
        label: '技能证书',
        hint: '语言、软件、证书等',
        done: form.skills.length > 0,
      },
      {
        id: 'projects',
        section: 'projects',
        label: '科研项目',
        hint: '项目名称、角色和你的工作',
        done: form.projects.length > 0,
      },
      {
        id: 'achievements',
        section: 'achievements',
        label: '成果',
        hint: '论文、专利、奖项等',
        done: form.achievements.length > 0,
      },
      {
        id: 'conferences',
        section: 'conferences',
        label: '会议参加',
        hint: '报告、海报或参会经历',
        done: form.conferences.length > 0,
        optional: true,
      },
      {
        id: 'internships',
        section: 'internships',
        label: '实习经历',
        hint: '单位、岗位和工作内容',
        done: form.internships.length > 0,
        optional: true,
      },
    ];
  }, [form]);

  const coreGuide = guide.filter((item) => !item.optional);
  const optionalGuide = guide.filter((item) => item.optional);
  const coreDone = coreGuide.filter((item) => item.done).length;
  const optionalDone = optionalGuide.filter((item) => item.done).length;
  const completion = coreGuide.length ? Math.round((coreDone / coreGuide.length) * 100) : 0;
  const openDraftCount = [
    educationDraftHasContent,
    skillDraftHasContent,
    projectDraftHasContent,
    conferenceDraftHasContent,
    internshipDraftHasContent,
  ].filter(Boolean).length;

  const jumpToSection = (section: EditorSection) => {
    const node = sectionRefs.current[section];
    if (!node) return;
    if (node instanceof HTMLDetailsElement) node.open = true;
    const header = document.querySelector('.topbar');
    const offset = (header?.getBoundingClientRect().height ?? 0) + 12;
    const top = window.scrollY + node.getBoundingClientRect().top - offset;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  if (me.isLoading) {
    return <div className="empty">正在读取简历资料…</div>;
  }

  if (!canEditResume(auth.data?.persona ?? 'visitor')) {
    return (
      <div className="empty">
        <b>当前角色没有简历编辑权限</b>
        只有管理员、组内教师和在读学生可以创建或编辑简历。
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="empty">
        <b>还没有为你建立简历资料</b>
        请联系管理员，在「简历审核」里把你的账号设为可发布，并分配一个 slug。
      </div>
    );
  }

  const published = profile.status === 'published';
  const pending = me.data?.pending ?? null;
  const skillCount = form.skills.length;

  return (
    <div className="resume-console">
      <div className="page-head">
        <div>
          <div className="eyebrow">My resume</div>
          <h1>我的简历</h1>
        </div>
        <div className="resume-status-row">
          <span className={`badge ${published ? 'ok' : ''}`}>{STATUS_LABEL[profile.status] ?? profile.status}</span>
          {pending ? (
            <>
              <span className="badge warn">有包待审核</span>
              <a
                className="btn btn-primary btn-sm"
                href={`/resume-preview/${pending.id}/`}
                target="_blank"
                rel="noreferrer"
              >
                预览最新版本
              </a>
            </>
          ) : null}
          {published ? (
            <a className="btn btn-ghost btn-sm" href={`/resume/${profile.slug}/`} target="_blank" rel="noreferrer">
              查看线上页面
            </a>
          ) : null}
        </div>
      </div>

      {notice ? <div className={`notice ${notice.kind}`}>{notice.text}</div> : null}

      <div className="resume-console-grid">
        <section className="panel resume-form">
          <div className="section-head">
            <h2>资料草稿</h2>
            <div className="resume-save-meta">
              <span
                className={`resume-save-state ${save.isPending ? 'saving' : saveState}`}
                aria-live="polite"
              >
                {save.isPending
                  ? '正在保存…'
                  : saveState === 'pending'
                    ? '修改待保存…'
                    : saveState === 'error'
                      ? `自动保存失败：${saveError}`
                      : saveState === 'blocked'
                        ? '请先填写姓名'
                        : saveState === 'saved'
                          ? '已保存'
                          : '修改后自动保存'}
              </span>
              {openDraftCount ? (
                <span className="resume-draft-chip">{openDraftCount} 处填写已暂存</span>
              ) : null}
            </div>
          </div>
          <p className="resume-form-hint">
            已提交的板块会自动保存；正在填写的条目也会暂存在本浏览器，刷新后可以继续。
          </p>

          <details
            className="resume-section"
            open
            ref={(node) => {
              sectionRefs.current.basics = node;
            }}
          >
            <summary>
              <span>基本资料</span>
              <span className="resume-section-hint">姓名、年级、方向</span>
            </summary>
            <div className="resume-section-body">
              <div className="field resume-portrait-field">
                <label htmlFor="resume-portrait">个人照片</label>
                <UploadField
                  id="resume-portrait"
                  kind="portrait"
                  value={form.portrait}
                  hint="JPEG / PNG / WebP，≤ 1.5 MB；复制提示词时会一起提供给 AI。"
                  onChange={(value) => setForm({ ...form, portrait: value })}
                />
              </div>
              <div className="field">
                <label htmlFor="resume-name">姓名</label>
                <input
                  id="resume-name"
                  value={form.displayName}
                  maxLength={32}
                  onChange={(event) => setForm({ ...form, displayName: event.target.value })}
                />
              </div>
              <div className="resume-field-row">
                <div className="field">
                  <label htmlFor="resume-grade">年级</label>
                  <input
                    id="resume-grade"
                    value={form.grade}
                    maxLength={30}
                    placeholder="2024级硕士"
                    onChange={(event) => setForm({ ...form, grade: event.target.value })}
                  />
                </div>
                <div className="field">
                  <label htmlFor="resume-program">专业</label>
                  <SearchSelect
                    id="resume-program"
                    value={form.program}
                    options={majorOptions}
                    placeholder="搜索或输入专业"
                    maxLength={40}
                    onChange={(value) => setForm({ ...form, program: value })}
                  />
                </div>
              </div>
              <div className="field">
                <label htmlFor="resume-direction">研究方向</label>
                <input
                  id="resume-direction"
                  value={form.direction}
                  maxLength={80}
                  placeholder="CO₂ 捕集与封存"
                  onChange={(event) => setForm({ ...form, direction: event.target.value })}
                />
              </div>
              <div className="field">
                <label htmlFor="resume-summary">简介</label>
                <textarea
                  id="resume-summary"
                  value={form.summary}
                  maxLength={300}
                  rows={4}
                  placeholder="一句话说明你在做什么。"
                  onChange={(event) => setForm({ ...form, summary: event.target.value })}
                />
              </div>
              <div className="resume-field-row">
                <div className="field">
                  <label htmlFor="resume-email">邮箱（可选）</label>
                  <input
                    id="resume-email"
                    value={form.email}
                    maxLength={120}
                    placeholder="name@example.edu"
                    onChange={(event) => setForm({ ...form, email: event.target.value })}
                  />
                </div>
                <label className="resume-check">
                  <input
                    type="checkbox"
                    checked={form.showEmail}
                    onChange={(event) => setForm({ ...form, showEmail: event.target.checked })}
                  />
                  在简历页公开邮箱
                </label>
              </div>
            </div>
          </details>

          <details
            className="resume-section"
            ref={(node) => {
              sectionRefs.current.education = node;
            }}
          >
            <summary>
              <span>教育背景</span>
              <span className="resume-section-hint">
                {form.education.length ? `${form.education.length} 项` : '未填写'}
                {educationDraftHasContent ? ' · 有未提交内容' : ''}
              </span>
            </summary>
            <div className="resume-section-body">
              {form.education.length ? (
                <ul className="resume-entry-list">
                  {form.education.map((item, index) => (
                    <li className="resume-entry" key={`${item.school}-${item.degree}-${index}`}>
                      <div className="resume-entry-main">
                        <b>{item.school || '未填写学校'}</b>
                        <span className="resume-entry-meta">
                          {[item.degree, item.major].filter(Boolean).join(' · ') || '未填写学历与专业'}
                        </span>
                        {item.start || item.end ? (
                          <span className="muted">
                            {[item.start, item.end].filter(Boolean).join(' – ')}
                          </span>
                        ) : null}
                      </div>
                      <div className="resume-entry-actions">
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => editEducation(index)}
                        >
                          编辑
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => removeEducation(index)}
                        >
                          删除
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : null}
              {editingEducation === null && entryForm !== 'education' ? (
                <div className="resume-section-head">
                  <span className="muted">
                    {form.education.length ? '可以继续添加教育经历。' : '还没有添加教育背景。'}
                  </span>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => openEntryForm('education')}
                  >
                    添加教育背景
                  </button>
                </div>
              ) : null}
              {editingEducation !== null || entryForm === 'education' ? (
                <div className="resume-entry-form">
                  <div className="resume-section-head">
                    <span className="muted">
                      {editingEducation === null ? '填写教育背景' : `编辑第 ${editingEducation + 1} 项`}
                    </span>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={cancelEducationEdit}
                    >
                      取消
                    </button>
                  </div>
                <div className="resume-field-row">
                  <div className="field">
                    <label htmlFor="resume-school">学校</label>
                    <SearchSelect
                      id="resume-school"
                      value={educationDraft.school}
                      options={schoolOptions}
                      placeholder="搜索或输入学校"
                      maxLength={60}
                      onChange={(value) => updateEducationDraft('school', value)}
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="resume-degree">学历</label>
                    <select
                      id="resume-degree"
                      value={educationDraft.degree}
                      onChange={(event) => updateEducationDraft('degree', event.target.value)}
                    >
                      <option value="">请选择</option>
                      {degreeOptions.map((degree) => (
                        <option key={degree} value={degree}>
                          {degree}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="field">
                  <label htmlFor="resume-major">专业</label>
                  <SearchSelect
                    id="resume-major"
                    value={educationDraft.major}
                    options={majorOptions}
                    placeholder="搜索或输入专业"
                    maxLength={40}
                    onChange={(value) => updateEducationDraft('major', value)}
                  />
                </div>
                <div className="resume-field-row">
                  <div className="field">
                    <label htmlFor="resume-edu-start-year">入学年月</label>
                    <YearMonthSelect
                      id="resume-edu-start"
                      value={educationDraft.start}
                      onChange={(value) => updateEducationDraft('start', value)}
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="resume-edu-end-year">毕业年月</label>
                    <YearMonthSelect
                      id="resume-edu-end"
                      value={educationDraft.end}
                      onChange={(value) => updateEducationDraft('end', value)}
                    />
                  </div>
                </div>
                {errors.education ? (
                  <p className="resume-field-error" role="alert">
                    {errors.education}
                  </p>
                ) : null}
                <div className="resume-actions">
                  <button type="button" className="btn btn-primary btn-sm" onClick={commitEducation}>
                    {editingEducation === null ? '添加' : '保存修改'}
                  </button>
                </div>
                </div>
              ) : null}
            </div>
          </details>

          <details
            className="resume-section"
            ref={(node) => {
              sectionRefs.current.skills = node;
            }}
          >
            <summary>
              <span>技能证书</span>
              <span className="resume-section-hint">
                {skillCount ? `${skillCount} 项` : '未填写'}
                {skillDraftHasContent ? ' · 有未提交内容' : ''}
              </span>
            </summary>
            <div className="resume-section-body">
              {form.skills.length ? (
                <div className="resume-skill-tags">
                  {form.skills.map((skill, index) => (
                    <span className="resume-skill-tag" key={`${skill}-${index}`}>
                      {skill}
                      <button
                        type="button"
                        className="resume-skill-remove"
                        aria-label={`删除 ${skill}`}
                        onClick={() => removeSkill(index)}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="muted resume-entry-empty">还没有添加技能证书。</p>
              )}
              <div className="field">
                <label htmlFor="resume-skill-input">添加一项</label>
                <div className="resume-skill-row">
                  <input
                    id="resume-skill-input"
                    value={skillDraft}
                    maxLength={60}
                    placeholder="例如：CET-6"
                    onChange={(event) => setSkillDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter') return;
                      event.preventDefault();
                      addSkill();
                    }}
                  />
                  <button type="button" className="btn btn-primary btn-sm" onClick={addSkill}>
                    添加
                  </button>
                </div>
              </div>
            </div>
          </details>

          <details
            className="resume-section resume-projects"
            ref={(node) => {
              sectionRefs.current.projects = node;
            }}
          >
            <summary>
              <span>科研项目</span>
              <span className="resume-section-hint">
                {form.projects.length ? `${form.projects.length} 项` : '未填写'}
                {projectDraftHasContent ? ' · 有未提交内容' : ''}
              </span>
            </summary>
            <div className="resume-section-body">
              {form.projects.length ? (
                <ul className="resume-entry-list">
                  {form.projects.map((project, index) => (
                    <li className="resume-entry" key={index}>
                      {project.photo ? (
                        <img className="resume-entry-thumb" src={uploadUrl(project.photo.id)} alt="" />
                      ) : null}
                      <div className="resume-entry-main">
                        <b>{project.name || '未填写项目名称'}</b>
                        <span className="resume-entry-meta">
                          {[project.role, project.period].filter(Boolean).join(' · ') ||
                            '未填写角色与时间'}
                        </span>
                        {project.description ? (
                          <span className="resume-entry-desc">{project.description}</span>
                        ) : null}
                      </div>
                      <EntryActions
                        confirming={pendingDelete?.kind === 'project' && pendingDelete.index === index}
                        onEdit={() => editProject(index)}
                        onDelete={() => requestDelete('project', index)}
                        onConfirm={confirmDelete}
                        onCancel={() => setPendingDelete(null)}
                      />
                    </li>
                  ))}
                </ul>
              ) : null}
              {editingProject === null && entryForm !== 'project' ? (
                <div className="resume-section-head">
                  <span className="muted">
                    {form.projects.length ? '可以继续添加科研项目。' : '还没有添加科研项目。'}
                  </span>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => openEntryForm('project')}
                  >
                    添加科研项目
                  </button>
                </div>
              ) : null}
            {editingProject !== null || entryForm === 'project' ? (
              <div className="resume-entry-form">
                <div className="resume-section-head">
                  <span className="muted">
                    {editingProject === null ? '填写科研项目' : `编辑第 ${editingProject + 1} 项`}
                  </span>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={cancelProjectEdit}>
                    取消
                  </button>
                </div>
              <div className="field">
                <label htmlFor="resume-project-name">项目名称</label>
                <input
                  id="resume-project-name"
                  value={projectDraft.name}
                  maxLength={80}
                  placeholder="CO₂ 捕集材料筛选"
                  onChange={(event) => updateProjectDraft({ name: event.target.value })}
                />
              </div>
              <div className="field">
                <label htmlFor="resume-project-role">担任角色</label>
                <input
                  id="resume-project-role"
                  value={projectDraft.role}
                  maxLength={40}
                  placeholder="主要参与人"
                  onChange={(event) => updateProjectDraft({ role: event.target.value })}
                />
              </div>
              <div className="resume-field-row">
                <div className="field">
                  <label htmlFor="resume-project-start-year">开始年月</label>
                  <YearMonthSelect
                    id="resume-project-start"
                    value={projectDraft.start}
                    onChange={(value) => updateProjectDraft({ start: value })}
                  />
                </div>
                <div className="field">
                  <label htmlFor="resume-project-end-year">结束年月</label>
                  <YearMonthSelect
                    id="resume-project-end"
                    value={projectDraft.end}
                    disabled={projectDraft.ongoing}
                    onChange={(value) => updateProjectDraft({ end: value })}
                  />
                  <label className="resume-check">
                    <input
                      id="resume-project-ongoing"
                      type="checkbox"
                      checked={projectDraft.ongoing}
                      onChange={(event) => {
                        const ongoing = event.target.checked;
                        updateProjectDraft(ongoing ? { ongoing, end: '' } : { ongoing });
                      }}
                    />
                    至今
                  </label>
                </div>
              </div>
              <div className="field">
                <label htmlFor="resume-project-desc">项目说明</label>
                <textarea
                  id="resume-project-desc"
                  value={projectDraft.description}
                  maxLength={300}
                  rows={3}
                  placeholder="你负责的工作和结果。"
                  onChange={(event) => updateProjectDraft({ description: event.target.value })}
                />
              </div>
              <div className="field">
                <label>项目照片</label>
                <UploadField
                  id="resume-project-photo"
                  kind="project"
                  value={projectDraft.photo}
                  hint="JPEG / PNG / WebP，≤ 1.5 MB。"
                  onChange={(value) => updateProjectDraft({ photo: value })}
                />
              </div>
              {errors.project ? (
                <p className="resume-field-error" role="alert">
                  {errors.project}
                </p>
              ) : null}
              <div className="resume-actions">
                <button type="button" className="btn btn-primary btn-sm" onClick={commitProject}>
                  {editingProject === null ? '添加项目' : '保存修改'}
                </button>
              </div>
              </div>
            ) : null}
            </div>
          </details>

          <details
            className="resume-section"
            ref={(node) => {
              sectionRefs.current.conferences = node;
            }}
          >
            <summary>
              <span>会议参加情况</span>
              <span className="resume-section-hint">
                {form.conferences.length ? `${form.conferences.length} 项` : '未填写'}
                {conferenceDraftHasContent ? ' · 有未提交内容' : ''}
              </span>
            </summary>
            <div className="resume-section-body">
              {form.conferences.length ? (
                <ul className="resume-entry-list">
                  {form.conferences.map((item, index) => (
                    <li className="resume-entry" key={index}>
                      {item.photo ? (
                        <img
                          className="resume-entry-thumb"
                          src={uploadUrl(item.photo.id)}
                          alt=""
                        />
                      ) : null}
                      <div className="resume-entry-main">
                        <b>{item.name || '未填写会议名称'}</b>
                        {item.topic ? (
                          <span className="resume-entry-meta">汇报题目：{item.topic}</span>
                        ) : null}
                        <span className="resume-entry-meta">
                          {[item.role, item.period, item.location].filter(Boolean).join(' · ') ||
                            '未填写参与形式与时间'}
                        </span>
                        {item.description ? (
                          <span className="resume-entry-desc">{item.description}</span>
                        ) : null}
                      </div>
                      <EntryActions
                        confirming={
                          pendingDelete?.kind === 'conference' && pendingDelete.index === index
                        }
                        onEdit={() => editConference(index)}
                        onDelete={() => requestDelete('conference', index)}
                        onConfirm={confirmDelete}
                        onCancel={() => setPendingDelete(null)}
                      />
                    </li>
                  ))}
                </ul>
              ) : null}
              {editingConference === null && entryForm !== 'conference' ? (
                <div className="resume-section-head">
                  <span className="muted">
                    {form.conferences.length ? '可以继续添加会议。' : '还没有添加会议参加情况。'}
                  </span>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => openEntryForm('conference')}
                  >
                    添加会议
                  </button>
                </div>
              ) : null}
              {editingConference !== null || entryForm === 'conference' ? (
                <div className="resume-entry-form">
                  <div className="resume-section-head">
                    <span className="muted">
                      {editingConference === null ? '填写会议' : `编辑第 ${editingConference + 1} 项`}
                    </span>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={cancelConferenceEdit}
                    >
                      取消
                    </button>
                  </div>
                <div className="field">
                  <label htmlFor="resume-conference-name">会议名称</label>
                  <input
                    id="resume-conference-name"
                    value={conferenceDraft.name}
                    maxLength={80}
                    placeholder="中国 CCS 大会"
                    onChange={(event) => updateConferenceDraft({ name: event.target.value })}
                  />
                </div>
                <div className="field">
                  <label htmlFor="resume-conference-topic">汇报题目</label>
                  <input
                    id="resume-conference-topic"
                    value={conferenceDraft.topic}
                    maxLength={120}
                    placeholder="口头报告或海报的题目"
                    onChange={(event) => updateConferenceDraft({ topic: event.target.value })}
                  />
                </div>
                <div className="resume-field-row">
                  <div className="field">
                    <label htmlFor="resume-conference-role">参与形式</label>
                    <select
                      id="resume-conference-role"
                      value={conferenceDraft.role}
                      onChange={(event) => updateConferenceDraft({ role: event.target.value })}
                    >
                      <option value="">请选择</option>
                      {conferenceRoleOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="field">
                    <label htmlFor="resume-conference-time-year">时间</label>
                    <YearMonthSelect
                      id="resume-conference-time"
                      value={conferenceDraft.period}
                      onChange={(value) => updateConferenceDraft({ period: value })}
                    />
                  </div>
                </div>
                <div className="field">
                  <label htmlFor="resume-conference-location">地点</label>
                  <input
                    id="resume-conference-location"
                    value={conferenceDraft.location}
                    maxLength={60}
                    placeholder="北京"
                    onChange={(event) => updateConferenceDraft({ location: event.target.value })}
                  />
                </div>
                <div className="field">
                  <label htmlFor="resume-conference-desc">会议说明</label>
                  <textarea
                    id="resume-conference-desc"
                    value={conferenceDraft.description}
                    maxLength={300}
                    rows={3}
                    placeholder="补充会议情况或你在会议上的工作。"
                    onChange={(event) => updateConferenceDraft({ description: event.target.value })}
                  />
                </div>
                <div className="field">
                  <label>参会照片</label>
                  <UploadField
                    id="resume-conference-photo"
                    kind="conference"
                    value={conferenceDraft.photo}
                    hint="报告现场、海报或参会证明，JPEG / PNG / WebP，≤ 1.5 MB。"
                    onChange={(value) => updateConferenceDraft({ photo: value })}
                  />
                </div>
                {errors.conference ? (
                  <p className="resume-field-error" role="alert">
                    {errors.conference}
                  </p>
                ) : null}
                <div className="resume-actions">
                  <button type="button" className="btn btn-primary btn-sm" onClick={commitConference}>
                    {editingConference === null ? '添加会议' : '保存修改'}
                  </button>
                </div>
                </div>
              ) : null}
            </div>
          </details>

          <details
            className="resume-section"
            ref={(node) => {
              sectionRefs.current.internships = node;
            }}
          >
            <summary>
              <span>实习经历</span>
              <span className="resume-section-hint">
                {form.internships.length ? `${form.internships.length} 项` : '未填写'}
                {internshipDraftHasContent ? ' · 有未提交内容' : ''}
              </span>
            </summary>
            <div className="resume-section-body">
              {form.internships.length ? (
                <ul className="resume-entry-list">
                  {form.internships.map((item, index) => (
                    <li className="resume-entry" key={index}>
                      {item.photo ? (
                        <img
                          className="resume-entry-thumb"
                          src={uploadUrl(item.photo.id)}
                          alt=""
                        />
                      ) : null}
                      <div className="resume-entry-main">
                        <b>{item.company || '未填写单位名称'}</b>
                        <span className="resume-entry-meta">
                          {[item.role, item.period].filter(Boolean).join(' · ') ||
                            '未填写岗位与时间'}
                        </span>
                        {item.description ? (
                          <span className="resume-entry-desc">{item.description}</span>
                        ) : null}
                      </div>
                      <EntryActions
                        confirming={
                          pendingDelete?.kind === 'internship' && pendingDelete.index === index
                        }
                        onEdit={() => editInternship(index)}
                        onDelete={() => requestDelete('internship', index)}
                        onConfirm={confirmDelete}
                        onCancel={() => setPendingDelete(null)}
                      />
                    </li>
                  ))}
                </ul>
              ) : null}
              {editingInternship === null && entryForm !== 'internship' ? (
                <div className="resume-section-head">
                  <span className="muted">
                    {form.internships.length ? '可以继续添加实习经历。' : '还没有添加实习经历。'}
                  </span>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => openEntryForm('internship')}
                  >
                    添加实习
                  </button>
                </div>
              ) : null}
              {editingInternship !== null || entryForm === 'internship' ? (
                <div className="resume-entry-form">
                  <div className="resume-section-head">
                    <span className="muted">
                      {editingInternship === null ? '填写实习经历' : `编辑第 ${editingInternship + 1} 项`}
                    </span>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={cancelInternshipEdit}
                    >
                      取消
                    </button>
                  </div>
                <div className="field">
                  <label htmlFor="resume-internship-company">单位名称</label>
                  <input
                    id="resume-internship-company"
                    value={internshipDraft.company}
                    maxLength={80}
                    placeholder="某能源公司"
                    onChange={(event) => updateInternshipDraft({ company: event.target.value })}
                  />
                </div>
                <div className="field">
                  <label htmlFor="resume-internship-role">岗位</label>
                  <input
                    id="resume-internship-role"
                    value={internshipDraft.role}
                    maxLength={40}
                    placeholder="研发实习生"
                    onChange={(event) => updateInternshipDraft({ role: event.target.value })}
                  />
                </div>
                <div className="resume-field-row">
                  <div className="field">
                    <label htmlFor="resume-internship-start-year">开始年月</label>
                    <YearMonthSelect
                      id="resume-internship-start"
                      value={internshipDraft.start}
                      onChange={(value) => updateInternshipDraft({ start: value })}
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="resume-internship-end-year">结束年月</label>
                    <YearMonthSelect
                      id="resume-internship-end"
                      value={internshipDraft.end}
                      disabled={internshipDraft.ongoing}
                      onChange={(value) => updateInternshipDraft({ end: value })}
                    />
                    <label className="resume-check">
                      <input
                        id="resume-internship-ongoing"
                        type="checkbox"
                        checked={internshipDraft.ongoing}
                        onChange={(event) => {
                          const ongoing = event.target.checked;
                          updateInternshipDraft(ongoing ? { ongoing, end: '' } : { ongoing });
                        }}
                      />
                      至今
                    </label>
                  </div>
                </div>
                <div className="field">
                  <label htmlFor="resume-internship-desc">实习说明</label>
                  <textarea
                    id="resume-internship-desc"
                    value={internshipDraft.description}
                    maxLength={300}
                    rows={3}
                    placeholder="你负责的工作和结果。"
                    onChange={(event) => updateInternshipDraft({ description: event.target.value })}
                  />
                </div>
                <div className="field">
                  <label>实习照片</label>
                  <UploadField
                    id="resume-internship-photo"
                    kind="internship"
                    value={internshipDraft.photo}
                    hint="工作环境、工牌或实习证明，JPEG / PNG / WebP，≤ 1.5 MB。"
                    onChange={(value) => updateInternshipDraft({ photo: value })}
                  />
                </div>
                {errors.internship ? (
                  <p className="resume-field-error" role="alert">
                    {errors.internship}
                  </p>
                ) : null}
                <div className="resume-actions">
                  <button type="button" className="btn btn-primary btn-sm" onClick={commitInternship}>
                    {editingInternship === null ? '添加实习' : '保存修改'}
                  </button>
                </div>
                </div>
              ) : null}
            </div>
          </details>

          <details
            className="resume-section"
            ref={(node) => {
              sectionRefs.current.achievements = node;
            }}
          >
            <summary>
              <span>成果</span>
              <span className="resume-section-hint">
                {form.achievements.length ? `${form.achievements.length} 项` : '未填写'}
              </span>
            </summary>
            <div className="resume-section-body">
              <div className="resume-section-head">
                <span className="muted">论文、专利、奖项或其他成果，可以添加多项</span>
                <button type="button" className="btn btn-ghost btn-sm" onClick={addAchievement}>
                  添加成果
                </button>
              </div>
              {form.achievements.length ? (
                <div className="resume-project-list">
                  {form.achievements.map((item, index) => (
                    <article className="resume-project" key={index}>
                      <div className="resume-section-head">
                        <span className="muted">成果 {index + 1}</span>
                        <div className="resume-entry-actions">
                          {pendingDelete?.kind === 'achievement' && pendingDelete.index === index ? (
                            <>
                              <span className="resume-delete-hint">删除后不可恢复</span>
                              <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                onClick={confirmDelete}
                              >
                                确认删除
                              </button>
                              <button
                                type="button"
                                className="btn btn-ghost btn-sm"
                                onClick={() => setPendingDelete(null)}
                              >
                                取消
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              onClick={() => setPendingDelete({ kind: 'achievement', index })}
                            >
                              删除
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="resume-field-row">
                        <div className="field">
                          <label htmlFor={`resume-achievement-title-${index}`}>成果名称</label>
                          <input
                            id={`resume-achievement-title-${index}`}
                            value={item.title}
                            maxLength={80}
                            placeholder="论文 / 专利 / 奖项"
                            onChange={(event) => updateAchievement(index, { title: event.target.value })}
                          />
                        </div>
                        <div className="field">
                          <label htmlFor={`resume-achievement-date-${index}-year`}>时间</label>
                          <YearMonthSelect
                            id={`resume-achievement-date-${index}`}
                            value={item.date}
                            onChange={(value) => updateAchievement(index, { date: value })}
                          />
                        </div>
                      </div>
                      <div className="field">
                        <label htmlFor={`resume-achievement-desc-${index}`}>成果说明</label>
                        <textarea
                          id={`resume-achievement-desc-${index}`}
                          value={item.description}
                          maxLength={300}
                          rows={3}
                          placeholder="成果内容、发表情况或你的贡献。"
                          onChange={(event) =>
                            updateAchievement(index, { description: event.target.value })
                          }
                        />
                      </div>
                      <div className="field">
                        <label>成果材料</label>
                        <UploadField
                          id={`resume-achievement-material-${index}`}
                          kind="achievement"
                          value={item.material}
                          allowPdf
                          hint="证书、录用通知或论文首页；图片 ≤ 1.5 MB，PDF ≤ 4 MB。"
                          onChange={(value) => updateAchievement(index, { material: value })}
                        />
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <p className="muted resume-entry-empty">还没有添加成果。</p>
              )}
            </div>
          </details>

          <div className="resume-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={save.isPending}
              onClick={submitDraft}
            >
              {save.isPending ? '保存中…' : '保存草稿'}
            </button>
            {!profile.canPublish ? <span className="muted">管理员还没有给你发布权限。</span> : null}
          </div>
          <p className="resume-form-hint resume-save-hint">
            「保存草稿」会把正在填写的内容一并提交并同步；只修改已提交的板块时，自动保存已经生效。
          </p>
        </section>

        <div className="resume-side">
          <section className="panel resume-guide">
            <div className="section-head">
              <h2>创作进度</h2>
              <span className={`badge ${completion === 100 ? 'ok' : ''}`}>{completion}%</span>
            </div>
            <div
              className="resume-progress"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={completion}
            >
              <span style={{ width: `${completion}%` }} />
            </div>
            <p className="resume-guide-caption">
              核心资料 {coreDone}/{coreGuide.length} 项已完成
              {optionalDone ? `，可选资料 ${optionalDone}/${optionalGuide.length} 项` : ''}。
            </p>
            <ul className="resume-guide-list">
              {guide
                .filter((item) => !item.parent)
                .map((item) => {
                  const children = guide.filter((child) => child.parent === item.id);
                  return (
                    <li key={item.id} className={item.done ? 'done' : ''}>
                      <button type="button" onClick={() => jumpToSection(item.section)}>
                        <span className="resume-guide-dot" aria-hidden="true">
                          {item.done ? '✓' : ''}
                        </span>
                        <span className="resume-guide-text">
                          <b>{item.label}</b>
                          <span>{item.hint}</span>
                        </span>
                        {item.optional ? <span className="resume-guide-optional">可选</span> : null}
                      </button>
                      {children.length ? (
                        <ul className="resume-guide-children">
                          {children.map((child) => (
                            <li key={child.id} className={child.done ? 'done' : ''}>
                              <button type="button" onClick={() => jumpToSection(child.section)}>
                                <span className="resume-guide-dot" aria-hidden="true">
                                  {child.done ? '✓' : ''}
                                </span>
                                <span className="resume-guide-text">
                                  <b>{child.label}</b>
                                  <span>{child.hint}</span>
                                </span>
                                {child.optional ? (
                                  <span className="resume-guide-optional">可选</span>
                                ) : null}
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </li>
                  );
                })}
            </ul>
            {openDraftCount ? (
              <p className="resume-guide-caption">
                有 {openDraftCount} 处填写已暂存，提交后会同步并计入完成度。
              </p>
            ) : null}
          </section>

          <section className="panel resume-publish">
            <div className="section-head">
              <h2>发布简历</h2>
              <span className="badge">{profile.slug}</span>
            </div>
            <ol className="resume-steps">
              <li>
                <b>选一个模板，复制提示词给 AI</b>
                <span>提示词会自动带上你的资料和上传权限，不用单独获取或撤销。</span>
              </li>
              <li>
                <b>补充你的其他要求</b>
                <span>可以直接告诉 AI 版式、重点或风格偏好；它完成简历后会自动上传，请保持本页打开。</span>
              </li>
              <li>
                <b>预览后再决定是否修改</b>
                <span>上传后右上角会出现「预览最新版本」；要改就再复制一次提示词。</span>
              </li>
            </ol>
            <div className="resume-permission">
              <span className={`badge ${token && grantLeft > 0 ? 'ok' : ''}`}>
                {token && grantLeft > 0
                  ? `上传权限剩余 ${formatCountdown(grantLeft)}`
                  : '复制提示词时自动授权'}
              </span>
              <span className="muted">
                {token && grantLeft > 0
                  ? '关闭页面或到期后自动失效，重新复制会刷新权限'
                  : '关闭页面自动失效，最长 30 分钟'}
              </span>
            </div>
          </section>

          <section className="panel resume-templates">
            <div className="section-head">
              <h2>模板与提示词</h2>
            </div>
            <p className="muted">选择自己需要的模板；复制后直接粘贴给 AI 即可。</p>
            <div className="resume-template-grid resume-template-grid-inline">
              {templates.map((template) => (
                <article className="resume-template-card" key={template.id}>
                  <div className="eyebrow">{template.name}</div>
                  <h3>{template.summary}</h3>
                  {template.highlights.length ? (
                    <ul className="resume-template-highlights">
                      {template.highlights.slice(0, 3).map((highlight) => (
                        <li key={highlight}>{highlight}</li>
                      ))}
                    </ul>
                  ) : null}
                  <div className="resume-actions">
                    <a
                      className="btn btn-ghost btn-sm"
                      href={`/resume/_templates/${template.index}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      预览模板
                    </a>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={Boolean(copyingId) || !profile.canPublish}
                      onClick={() => void copyPrompt(template)}
                    >
                      {copyingId === template.id
                        ? '正在复制…'
                        : copiedId === template.id
                          ? '已复制'
                          : '复制提示词给 AI'}
                    </button>
                  </div>
                </article>
              ))}
            </div>
            <p className="resume-template-hint">
              复制提示词会自动获取上传权限；重新复制同一模板会刷新有效期，最多可成功上传 3 次。
            </p>
          </section>
        </div>
      </div>

      <section className="panel">
        <div className="section-head">
          <h2>发布记录</h2>
          <span className="muted">最近 20 次</span>
        </div>
        {me.data?.publishes.length ? (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>版本</th>
                  <th>状态</th>
                  <th>文件</th>
                  <th>时间</th>
                  <th>审核意见</th>
                </tr>
              </thead>
              <tbody>
                {me.data.publishes.map((item) => (
                  <tr key={item.id}>
                    <td>{item.id.slice(0, 8)}</td>
                    <td>
                      {PUBLISH_LABEL[item.status] ?? item.status}
                      {item.status === 'pending' ? (
                        <>
                          {' '}
                          <a href={`/resume-preview/${item.id}/`} target="_blank" rel="noreferrer">
                            预览
                          </a>
                        </>
                      ) : null}
                    </td>
                    <td>
                      {item.fileCount} 个 · {formatBytes(item.bytes)}
                    </td>
                    <td>{formatTime(item.publishedAt ?? item.createdAt)}</td>
                    <td>{item.reviewNote || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty">还没有发布记录。按上面的三步做一次试试。</div>
        )}
      </section>
    </div>
  );
}
